import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Lock, Shield, Workflow } from 'lucide-react'
import { AfosLogo } from '../components/Logo'
import { OrbitField } from '../components/OrbitField'
import { marketingSiteUrl, resolveHostMode } from '../lib/hostMode'

/** Dense product gateway for afos360.ai — Item 2 wire phase. */
export function GatewayLanding() {
  const reduce = useReducedMotion()
  const mode = resolveHostMode()
  const marketingHref = marketingSiteUrl(mode)

  return (
    <div className="afos-shell relative min-h-screen">
      <OrbitField />
      <header className="relative z-20 border-b border-[var(--color-line)] bg-black/25 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <AfosLogo size={30} />
          <a
            href={marketingHref}
            className="text-sm text-[var(--color-mist)] underline-offset-2 hover:text-[var(--color-foam)] hover:underline"
          >
            What is AFOS?
          </a>
        </div>
      </header>

      <main className="relative z-10 mx-auto flex min-h-[calc(100svh-4.5rem)] max-w-6xl flex-col justify-center px-4 py-16 sm:px-6">
        <div className="pointer-events-none absolute inset-0 -z-0">
          <motion.div
            className="absolute -right-[10%] top-[12%] h-[55vmin] w-[55vmin] rounded-full border border-[rgba(46,230,197,0.2)]"
            animate={reduce ? undefined : { rotate: 360 }}
            transition={{ duration: 70, repeat: Infinity, ease: 'linear' }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative max-w-2xl"
        >
          <p className="font-[family-name:var(--font-display)] text-[clamp(2.8rem,10vw,5.5rem)] font-extrabold leading-[0.9] tracking-[-0.04em]">
            <span className="text-gradient">Open the factory.</span>
          </p>
          <h1 className="mt-5 font-[family-name:var(--font-display)] text-[clamp(1.4rem,3.5vw,2.1rem)] font-bold tracking-tight text-[var(--color-foam)]">
            Your AI agent teams are waiting.
          </h1>
          <p className="mt-4 max-w-lg text-base text-[var(--color-mist)] sm:text-lg">
            Sign in to your tenant workspace — product lifecycle, shop CSAT, ephemeral
            co-build, human merge gates. Marketing story lives on afos360.com.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link
              to="/signin"
              className="neon-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[var(--color-foam)] px-7 py-3 text-base font-semibold text-[var(--color-void)]"
            >
              Sign in
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/signup"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[var(--color-line)] px-7 py-3 text-base text-[var(--color-foam)]"
            >
              Create organization
            </Link>
          </div>

          <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm text-[var(--color-mist)]">
            <li className="inline-flex items-center gap-2">
              <Shield className="h-4 w-4 text-[var(--color-cyan)]" /> SSO ready
            </li>
            <li className="inline-flex items-center gap-2">
              <Lock className="h-4 w-4 text-[var(--color-amber)]" /> Human merge gate
            </li>
            <li className="inline-flex items-center gap-2">
              <Workflow className="h-4 w-4 text-[var(--color-cyan)]" /> Multi-cloud
            </li>
          </ul>

          {mode.vanitySlug && (
            <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[var(--color-mist)]">
              Workspace · {mode.vanitySlug}.afos360.ai
            </p>
          )}
        </motion.div>
      </main>
    </div>
  )
}
